/* eslint-disable react-hooks/rules-of-hooks */
import {
  BaseBoxShapeUtil,
  HTMLContainer,
  T,
  toDomPrecision,
  assetIdValidator,
  useIsEditing,
  TLVideoAsset,
  TLAssetId,
  TLOnBeforeUpdateHandler,
  TLOnTranslateHandler,
} from '@tldraw/tldraw'
import classNames from 'classnames'
import { ReactEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { VideoShape, VideoShapeProps } from './Types'
import { Spinner } from 'common/Spinner/Spinner'
import { getDataURIFromURL, noCacheUrl } from '../ImageShape/helpers'
import { fitAndPosition } from 'object-fit-math'
import { FrameShape } from '../FrameShape/Types'
import { videoShapeMigrations } from './VideoShape'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { getAutoplayVideo, getIsPreview } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { FitMode } from 'object-fit-math/dist/types'

/** @public */
export class VideoShapeUtil extends BaseBoxShapeUtil<VideoShape> {
  static override type = 'video' as const
  static override props = {
    w: T.positiveNumber,
    h: T.positiveNumber,
    assetId: assetIdValidator.nullable(),
    time: T.positiveNumber,
    playing: T.boolean,
    thumbnail_url: T.nullable(T.string),
    isReplaceable: T.boolean,
    wasReplaced: T.boolean,
    duration: T.positiveNumber,
    isEditable: T.boolean,
    isAutoUpdatable: T.boolean,
  }

  static override migrations = videoShapeMigrations

  shouldDeselectAfterTranslating = false

  override canEdit = () => true
  override canCrop = () => false
  override isAspectRatioLocked = () => true
  override canResize = () => false
  override hideRotateHandle = () => true

  override getDefaultProps(): VideoShapeProps {
    return {
      w: 100,
      h: 100,
      assetId: null,
      time: 0,
      playing: true,
      thumbnail_url: null,
      isReplaceable: false,
      wasReplaced: false,
      duration: 0,
      isEditable: true,
      isAutoUpdatable: true,
    }
  }

  component(shape: VideoShape) {
    const { editor } = this
    const showControls = editor.getShapeGeometry(shape).bounds.w * editor.getZoomLevel() >= 110
    const asset = shape.props.assetId ? this.editor.getAsset(shape.props.assetId) : undefined
    const url = asset?.props.src
    const isEditing = useIsEditing(shape.id)
    const { isMobile } = useResponsiveDesign()
    const isPreview = getIsPreview(editor)
    const autoplayVideo = getAutoplayVideo(editor)
    const isMobilePreview = isMobile && isPreview
    const rVideo = useRef<HTMLVideoElement | null>(null)

    const [isLoaded, setIsLoaded] = useState(false)

    const handleLoadedData = useCallback<ReactEventHandler<HTMLVideoElement>>((e) => {
      const video = e.currentTarget
      if (!video) return

      setIsLoaded(true)
    }, [])

    // If the current time changes and we're not editing the video, update the video time
    useEffect(() => {
      const video = rVideo.current
      if (!video) return

      if (isEditing) {
        if (document.activeElement !== video) {
          video.focus()
        }
      }
    }, [isEditing, isLoaded])

    return (
      <HTMLContainer
        id={shape.id}
        style={{
          color: 'var(--color-text-3)',
          backgroundColor: asset ? 'transparent' : 'var(--color-low)',
          border: asset ? 'none' : '1px solid var(--color-low-border)',
          width: shape.props.w,
          height: shape.props.h,
        }}
      >
        <div
          className="tl-counter-scaled"
          onPointerDown={() => {
            if (isMobile) {
              const shapeUtil = this.editor.getShapeUtil('video') as VideoShapeUtil
              shapeUtil.shouldDeselectAfterTranslating = this.editor.getSelectedShapes().length === 0
            }
          }}
        >
          <div className="tl-video-container">
            {!asset && <div>Error: No video found</div>}
            {asset && isMobilePreview && shape.props.thumbnail_url && (
              <img src={shape.props.thumbnail_url} alt="Video thumbnail" width="100%" height="100%" />
            )}
            {asset && !isMobilePreview && url && (
              <>
                <video
                  ref={rVideo}
                  style={{
                    pointerEvents: isEditing ? 'all' : undefined,
                    display: !isLoaded ? 'none' : undefined,
                  }}
                  className={classNames('tl-video', `tl-video-shape-${shape.id.split(':')[1]}`)}
                  width="100%"
                  height="100%"
                  draggable={false}
                  playsInline={autoplayVideo}
                  autoPlay={autoplayVideo}
                  muted
                  loop={autoplayVideo}
                  disableRemotePlayback
                  disablePictureInPicture
                  controls={isEditing && showControls}
                  onLoadedData={handleLoadedData}
                  hidden={!isLoaded}
                >
                  <source src={url} />
                </video>
                {!isLoaded && <Spinner />}
              </>
            )}
          </div>
        </div>
      </HTMLContainer>
    )
  }

  override onBeforeUpdate: TLOnBeforeUpdateHandler<VideoShape> = (prev, next: VideoShape) => {
    const parent = this.editor.getShape<FrameShape>(next.parentId)
    if (!parent) return

    if (!prev.props.isEditable) {
      return { ...prev, props: { ...prev.props, isEditable: next.props.isEditable } }
    }

    const { w: parentW, h: parentH } = parent.props
    const { x: nextX, y: nextY } = next
    const { w, h } = next.props

    let newX = nextX
    let newY = nextY

    if (w === parentW) {
      newX = 0

      if (h > parentH) {
        newY = Math.max(Math.min(nextY, 0), parentH - h)
      } else {
        newY = Math.max(0, Math.min(nextY, parentH - h))
      }
    } else if (h === parentH) {
      newY = 0

      if (w > parentW) {
        newX = Math.max(Math.min(nextX, 0), parentW - w)
      } else {
        newX = Math.max(0, Math.min(nextX, parentW - w))
      }
    } else {
      newX = Math.max(Math.min(nextX, 0), parentW - w)
      newY = Math.max(Math.min(nextY, 0), parentH - h)
    }

    return {
      ...next,
      x: newX,
      y: newY,
      opacity: 1,
      props: {
        ...next.props,
      },
    }
  }

  onTranslate: TLOnTranslateHandler<VideoShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  onTranslateEnd = () => {
    if (this.shouldDeselectAfterTranslating) {
      this.editor.selectNone()
    }
    this.shouldDeselectAfterTranslating = false
  }

  indicator(shape: VideoShape) {
    return <rect width={toDomPrecision(shape.props.w)} height={toDomPrecision(shape.props.h)} />
  }

  /**
   * Replaces the video with a new one with the given assetId.
   */
  replace = async (shape: VideoShape, assetId: TLAssetId, props?: Partial<VideoShapeProps>, fit?: FitMode) => {
    const asset = this.editor.getAsset(assetId) as TLVideoAsset | undefined
    if (!asset) return

    const currentBoundingBox = this.getGeometry(shape).getBounds()
    const fittedVideo = fitAndPosition(
      { width: currentBoundingBox.w, height: currentBoundingBox.h },
      { width: asset.props.w, height: asset.props.h },
      fit ?? 'contain',
      '50%',
      '50%',
    )
    this.editor.batch(() => {
      this.editor.deleteShapes([shape.id])
      this.editor.createShape({
        type: 'video',
        parentId: shape.parentId,
        x: shape.x + fittedVideo.x,
        y: shape.y + fittedVideo.y,
        index: shape.index,
        props: {
          ...shape.props,
          ...props,
          assetId: asset.id,
          w: fittedVideo.width,
          h: fittedVideo.height,
        },
      })
    })
  }

  /**
   * Updates the isReplaceable property of the shape and sets the wasReplaced property to false
   * @param shape The shape to update
   * @param isReplaceable The new value of isReplaceable
   * @returns void
   */
  setReplaceable = (shape: VideoShape, isReplaceable: boolean) => {
    this.editor.updateShapes([
      {
        ...shape,
        props: {
          isReplaceable,
          wasReplaced: false,
        },
      },
    ])
  }

  /**
   * Returns true if the shape can be replaced and has not been replaced yet
   * @param shape The shape to check
   */
  canBeReplaced = (shape: VideoShape) => shape.props.isReplaceable && !shape.props.wasReplaced

  /**
   * Returns true if the shape cannot be replaced and is not a background image
   * @param shape The shape to check
   */
  canReplace = (shape: VideoShape) => {
    return !this.canBeReplaced(shape)
  }

  override async toSvg(shape: VideoShape) {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    const thumbnailUrl = shape.props.thumbnail_url
    const thumbnailSrc = thumbnailUrl ? await getDataURIFromURL(noCacheUrl(thumbnailUrl)) : ''

    if (thumbnailSrc.length > 0) {
      const thumbnail = document.createElementNS('http://www.w3.org/2000/svg', 'image')
      thumbnail.setAttributeNS('http://www.w3.org/1999/xlink', 'href', thumbnailSrc)
      thumbnail.setAttribute('width', shape.props.w.toString())
      thumbnail.setAttribute('height', shape.props.h.toString())
      g.appendChild(thumbnail)
    } else {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      rect.setAttribute('width', shape.props.w.toString())
      rect.setAttribute('height', shape.props.h.toString())
      rect.setAttribute('fill', 'var(--color-low)')
      g.appendChild(rect)
    }

    return g
  }
}
